import { DEFAULT_SPICE_LEVEL_VALUE } from "../../../utils/Constants";
import { extractInclusiveTax } from "../../../utils/commonfunction";
import { unitsOf } from "../../../utils/addonQuantity";

/**
 * Build the `item.addons` array a set-menu (SET_OF_ITEMS) order line sends to
 * the backend, from the cashier's per-group choices. One entry per choice group,
 * each carrying the chosen item's id + price fields + spice level. `item_id` is
 * what the backend re-hydrates the authoritative component from.
 *
 * @param {Array} groups              - resolved choice groups from the cashier
 *                                      menu-item-details endpoint, each
 *                                      `{ option_id, addon_name, items: [...] }`.
 * @param {Object} selectionsByIndex  - map of group ARRAY INDEX -> chosen
 *                                      `{ item, spice_level, quantity }`
 *                                      (spice_level is the chosen spice OBJECT,
 *                                      or null/absent; quantity defaults to 1).
 *                                      Keyed by index, not option_id: the same
 *                                      group can appear twice and each instance
 *                                      is its own ordered line.
 *
 * Quantity is emitted as REPEAT ENTRIES, not a `quantity` field: the backend
 * prices each entry and sums them (`resolveOrderAddons` + `sumSetLine`), and
 * every downstream reader — receipts, kitchen tickets, split payment — already
 * works off the resulting line. A count field would have to be threaded through
 * all of them for the same billed total.
 * @param {*} defaultSpiceLevel       - fallback spice for a spice-needed item
 *                                      with no explicit selection.
 * @returns {Array} full addons array (never partial); [] when no valid choice,
 *                  so callers can preserve the no-submit guard.
 */
export const buildSetMenuAddons = (
  groups = [],
  selectionsByIndex = {},
  defaultSpiceLevel = DEFAULT_SPICE_LEVEL_VALUE
) =>
  groups.reduce((addons, group, index) => {
    const selection = selectionsByIndex[index];
    const chosen = selection?.item;
    if (!chosen?._id) return addons;

    const needsSpice = !!chosen.spice_level_needed;
    const entry = {
      item_id: chosen._id,
      item_name: chosen.item_name,
      item_name_ja: chosen.item_name_ja,
      price: chosen.price,
      tax_price: chosen.tax_price,
      total_price: chosen.total_price,
      consumption_tax_rate: chosen.consumption_tax_rate,
      spice_level: needsSpice
        ? selection?.spice_level || defaultSpiceLevel
        : DEFAULT_SPICE_LEVEL_VALUE,
      spice_level_needed: needsSpice,
      addon_id: group.option_id,
      addon_name: group.addon_name,
    };
    const quantity = unitsOf(selection);
    for (let i = 0; i < quantity; i++) {
      addons.push({ ...entry });
    }
    return addons;
  }, []);

/**
 * Combo price for a set line: its GROSS is the base set's gross plus every
 * chosen component's gross (each already net + its tax, as the picker showed
 * it), and the tax is EXTRACTED from that total in one pass at the parent's
 * rate, rounded by the restaurant's `tax_when_register_product` setting.
 *
 * So `total_price` is exactly the sum of the prices the cashier was shown, and
 * tax is a breakdown of it rather than something added on top. A set with no
 * premium components reproduces its stored price exactly, because this is the
 * same extraction menu registration used on the same gross.
 *
 * Mirrors the backend `sumSetLine` (setMenuPricing.ts) exactly — the server
 * re-derives the line authoritatively on add, so any drift here would show the
 * cashier a total the order does not settle at.
 */
export const calculateSetMenuPrice = (item, addons = [], taxRoundType) => {
  const grossTotal =
    (Number(item?.total_price) || 0) +
    addons.reduce((sum, a) => sum + (Number(a?.total_price) || 0), 0);

  const { net, tax } = extractInclusiveTax(grossTotal, item?.consumption_tax_rate, taxRoundType);

  return { price: net, tax_price: tax, total_price: grossTotal };
};
